import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatAutocompleteSelectedEvent, MatDialogRef, MAT_DIALOG_DATA, MatSelectChange } from '@angular/material';

import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { debounceTime } from 'rxjs/operators/debounceTime';
import { distinctUntilChanged } from 'rxjs/operators/distinctUntilChanged';
import { fromEvent } from 'rxjs/observable/fromEvent';
import { map } from 'rxjs/operators/map';
import { Observable } from 'rxjs/Observable';
import { takeUntil } from 'rxjs/operators/takeUntil';
import { Subject } from 'rxjs/Subject';

import { ApplicationService, CollegeInfo, CollegeCourseInfo, CollegeDepartmentInfo } from '../../application/application.service';
import { UserService, UserInfo } from '../../user/user.service';

@Component({
    selector: 'dialog-adoptioncoursesearch-component',
    styleUrls: ['adoptioncoursesearch.dialogcomponent.css'],
    templateUrl: 'adoptioncoursesearch.dialogcomponent.html',
})
export class AdoptionCourseSearchDialogComponent implements OnInit, OnDestroy {
    @ViewChild('filter') filter: ElementRef;

    private _subRelease: Subject<void> = new Subject<void>();

    title: string;
    message: string;
    oktext: string;
    canceltext: string;

    AvailableColleges: CollegeInfo[];
    CourseChoice: CollegeCourseInfo;
    Courses: CollegeCourseInfo[];
    CourseSearch: string;
    CurrentSchoolYear: string;
    DepartmentChoice: number;
    Departments: CollegeDepartmentInfo[];
    FilteredCourses: CollegeCourseInfo[];
    SelectedCollege: CollegeInfo;
    User: UserInfo;

    constructor(
        public dialogRef: MatDialogRef<AdoptionCourseSearchDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: BaseAdoptionCourseSearchDialogOptions,
        private appService: ApplicationService,
        private userService: UserService
    ) {
        this.title = data.TitleText || 'Please wait...';
        this.message = data.MessageText || '';
        this.oktext = data.OKText || '';
        this.canceltext = data.CancelText || '';

        this.AvailableColleges = data.Colleges;
        this.CurrentSchoolYear = data.CurrentSchoolYear;
        this.SelectedCollege = data.SelectedCollege;
    }

    CancelHandler() {
        this.dialogRef.close(null);
    }

    ngOnDestroy() {
        this._subRelease.next();
        this._subRelease.complete();
    }

    ngOnInit() {
        const self = this;

        // Set up constant looping for the user object
        self.userService.UserSubject.pipe(takeUntil(self._subRelease)).subscribe(_user => {
            self.User = _user;
        });

        fromEvent(this.filter.nativeElement, 'keyup')
            .pipe(
                takeUntil(this._subRelease),
                debounceTime(150),
                distinctUntilChanged()
            )
            .subscribe(() => {
                const newvalue = this.filter.nativeElement.value.toLowerCase();

                const fcourses = this.Courses.filter(item => {
                    let keep = false;

                    if (item.FullCode.toLowerCase().indexOf(newvalue) >= 0) {
                        keep = true;
                    }
                    if (!keep && item.Corequisites.toLowerCase().indexOf(newvalue) >= 0) {
                        keep = true;
                    }
                    if (!keep && item.Description.toLowerCase().indexOf(newvalue) >= 0) {
                        keep = true;
                    }
                    if (!keep && item.Name.toLowerCase().indexOf(newvalue) >= 0) {
                        keep = true;
                    }
                    if (!keep && item.Prerequisites.toLowerCase().indexOf(newvalue) >= 0) {
                        keep = true;
                    }

                    return keep;
                });

                this.FilteredCourses = fcourses;
            });

        // Load the initial courses and departments for the currently selected college
        if (self.SelectedCollege) {
            self.UpdateCollege(<MatSelectChange>{ value: self.SelectedCollege });
        }
    }

    OKHandler() {
        // The user has chosen to select the course. Return the course selected.
        this.dialogRef.close(this.CourseChoice);
    }

    // #region Autocomplete Selected Method

    AutocompleteSelected(course: MatAutocompleteSelectedEvent) {
        const self = this;

        if (!course || !course.option) {
            return;
        }

        // Scan through and if we have a department, select it, and then select the course as well.
        const newoption = <CollegeCourseInfo>course.option.value;

        if (newoption.Department != null) {
            self.DepartmentChoice = newoption.Department;

            if (self.Departments && self.Departments[self.DepartmentChoice]) {
                const newcourse = self.Departments[self.DepartmentChoice].Courses.find(item => {
                    return item.DualEnrollCollegeCourseID === newoption.DualEnrollCollegeCourseID;
                });
                if (newcourse) {
                    self.CourseChoice = newcourse;
                }
            }
        }
    }

    // #endregion

    // #region Display Function Method

    DisplayFn(course: CollegeCourseInfo) {
        return course ? `${course.Prefix} ${course.Code} - ${course.Name}` : course;
    }

    // #endregion

    // #region Update College Method

    UpdateCollege(event: MatSelectChange) {
        const self = this;

        if (!event || !event.value) {
            return;
        }

        const col = <CollegeInfo>event.value;

        // Call to the app service to load the courses for this college.
        self.appService
            .GetAllCoursesAtCollege(self.User.AuthToken, col.CollegeID, '20' + self.CurrentSchoolYear.substr(2))
            .subscribe(courseIdx => {
                if (courseIdx != null) {
                    this.Courses = self.appService.CollegeCourses[courseIdx].Courses || [];
                    this.Departments = self.appService.CollegeCourses[courseIdx].Departments || [];
                    this.FilteredCourses = <CollegeCourseInfo[]>(
                        JSON.parse(JSON.stringify(self.appService.CollegeCourses[courseIdx].Courses))
                    );
                }
            });
    }

    // #endregion

    // #region Update Filter Method

    UpdateFilter(event: MatSelectChange) {
        if (!event || !event.value) {
            return;
        }

        const course = <CollegeCourseInfo>event.value;
        this.filter.nativeElement.value = `${course.Prefix} ${course.Code} - ${course.Name}`;
    }

    // #endregion
}

// #region Custom Classes

export class BaseAdoptionCourseSearchDialogOptions {
    TitleText?: string;
    MessageText?: string;
    OKText?: string;
    CancelText?: string;
    Colleges?: CollegeInfo[];
    Courses?: CollegeCourseInfo[];
    CurrentSchoolYear?: string;
    Departments?: CollegeDepartmentInfo[];
    SelectedCollege?: CollegeInfo;
}

// #endregion



// WEBPACK FOOTER //
// ./src/services/dialogs/adoptioncoursesearch/adoptioncoursesearch.dialogcomponent.ts